import { calculatePlanogramItemsPositions } from '../planograms/planogram';
import { POS_TYPE } from '../../constants';
import { getExpiryDatesBlockers, shouldShowExpiryBeforeNextServiceWarning } from '../serviceDocket/helpers';
import { getLocalTime } from '../time/time';

const DEFAULT_EXPIRY_THRESHOLD_DAYS = 7;

const getExpiryWarningsStatus = ({
	expiredProductsCount = 0,
	expireTodayCount = 0,
	unknownExpiryCount = 0,
	productsExpiringWithinXDaysCount = 0,
	expiringBeforeNextServiceCount = 0,
} = {}) => {
	if (expiredProductsCount > 0 || expireTodayCount > 0 || expiringBeforeNextServiceCount > 0) {
		return 'red';
	}
	if (productsExpiringWithinXDaysCount > 0 || unknownExpiryCount > 0) {
		return 'amber';
	}
	return 'none';
};

const getMinISODate = (a, b) => {
	if (!a) {
		return b || null;
	}
	if (!b) {
		return a || null;
	}
	return new Date(a) <= new Date(b) ? a : b;
};

const mergeExpiryWarnings = (prev = {}, next = {}) => {
	const merged = {
		expiredProductsCount: (prev.expiredProductsCount || 0) + (next.expiredProductsCount || 0),
		expireTodayCount: (prev.expireTodayCount || 0) + (next.expireTodayCount || 0),
		unknownExpiryCount: (prev.unknownExpiryCount || 0) + (next.unknownExpiryCount || 0),
		productsExpiringWithinXDaysCount: (prev.productsExpiringWithinXDaysCount || 0) + (next.productsExpiringWithinXDaysCount || 0),
		expiringBeforeNextServiceCount: (prev.expiringBeforeNextServiceCount || 0) + (next.expiringBeforeNextServiceCount || 0),
		nextServiceDate: prev.nextServiceDate || next.nextServiceDate || null,
		soonestExpiryBeforeNextService: getMinISODate(prev.soonestExpiryBeforeNextService, next.soonestExpiryBeforeNextService),
	};

	merged.status = getExpiryWarningsStatus(merged);

	return merged;
};

const getExpiryDatesArray = ({ expiryDates, quantity, isMarket }) => {
	if (!expiryDates || !quantity) {
		return [];
	}

	if (isMarket) {
		return [expiryDates?.[1] || null];
	}

	const dates = [];
	for (let i = 1; i <= quantity; i += 1) {
		dates.push(expiryDates?.[i] || null);
	}
	return dates;
};

const getExpiringBeforeNextService = ({
	expiryDates,
	quantity,
	isMarket,
	tenantTimezone,
	serviceRunPlanned,
	serviceFrequencyDays,
}) => {
	if (!expiryDates || !quantity || !serviceRunPlanned) {
		return {
			expiringBeforeNextServiceCount: 0,
			nextServiceDate: null,
			soonestExpiryBeforeNextService: null,
		};
	}

	const resolvedTimezone = tenantTimezone || 'Australia/Melbourne';
	const currentDate = getLocalTime(resolvedTimezone).startOf('day');

	// Use averageDaysSinceLastService as a proxy for service frequency (rounded to whole days).
	const rawServiceFrequencyDays = Number(serviceFrequencyDays);
	const resolvedServiceFrequencyDays = Number.isFinite(rawServiceFrequencyDays) && rawServiceFrequencyDays > 0
		? Math.max(1, Math.round(rawServiceFrequencyDays))
		: 7;

	const plannedDate = getLocalTime(resolvedTimezone, serviceRunPlanned).startOf('day');
	const nextServiceDate = plannedDate.clone().add(resolvedServiceFrequencyDays, 'days');

	const totalItemsToCheck = isMarket ? 1 : quantity;
	let expiringBeforeNextServiceCount = 0;
	let soonestExpiryBeforeNextService = null;

	for (let i = 1; i <= totalItemsToCheck; i += 1) {
		const expiryDate = expiryDates?.[i];
		if (!expiryDate) {
			continue;
		}

		const localExpiry = getLocalTime(resolvedTimezone, expiryDate).startOf('day');
		if (!localExpiry.isAfter(currentDate)) {
			continue;
		}
		if (localExpiry.isBefore(nextServiceDate)) {
			expiringBeforeNextServiceCount += 1;
			soonestExpiryBeforeNextService = getMinISODate(soonestExpiryBeforeNextService, localExpiry.toISOString());
		}
	}

	return {
		expiringBeforeNextServiceCount,
		nextServiceDate: nextServiceDate.toISOString(),
		soonestExpiryBeforeNextService,
	};
};

/*

This function accepts pickListItems, an array such as the following:

[
	product: { ... },
	removedProduct: { ... },
	planogramItem: { ... },
	quantity: 1,
	unadjustedQuantity: 1,
	selection: 1,
	machine: { ... },
]

However, these 'picked items' are for single planogram items, across many machines.
This function consolidates them into a summary form.
It returns an array of the following shape:

[
	{
		productName: '...',
		productImage: '...',
		pickAmount: 1,
		pickOrder: 1,
	},
	{
		productName: '...',
		productImage: '...',
		pickAmount: 1,
		pickOrder: 1,
	},
]

*/

export default function (pickListItems) {
	// Create a product object
	const groupedItems = pickListItems.reduce((acc, item) => {
		// Do not create summary items for pick
		// items that have no product.
		if (!item.product || (item.quantity + item.extraQuantity <= 0)) {
			return acc;
		}

		// Init product if doesn't exist yet
		if (!acc[item.product.id]) {
			acc[item.product.id] = {
				productId: item.product.id,
				productName: item.product.name,
				quantity: 0,
				productImgUrl: item.product.imageUrl || null,
				measurementUnit: item.product.measurementUnit,
				isIngredient: item.planogramItem.isIngredient || false,
				pickedQuantity: 0,
				machinePositions: [],
				extraQuantity: 0,
				pickedExtraQuantity: 0,
				pickOrder: item.pickOrder,
			};
		}

		// Update product quantity if stock item
		// as we have distributed extra quantity condition needed no more
		acc[item.product.id].quantity = acc[item.product.id].quantity + item.quantity;
		acc[item.product.id].extraQuantity = acc[item.product.id].extraQuantity + item.extraQuantity;
		if (item.isPicked) {
			acc[item.product.id].pickedQuantity = acc[item.product.id].pickedQuantity + item.quantity;
		}

		if (item.extraQuantityIsPicked) {
			acc[item.product.id].pickedExtraQuantity = acc[item.product.id].pickedExtraQuantity + item.extraQuantity;
		}

		acc[item.product.id].machinePositions.push(item.planogramItem.row);

		return acc;
	}, {});

	return Object.values(groupedItems);
}

export function generatePickListSummaryFromDockets (serviceDockets, shouldComputeMachinePosition, showAllSelections, expiryWarningsConfig = {}) {
	// Optionally compute expiry warnings per product + per machine (docket).
	// This is controlled by a pick list summary setting so we only do the work when needed.
	const shouldComputeExpiryWarnings = Boolean(expiryWarningsConfig?.enabled && expiryWarningsConfig?.tenantSettings?.useExpiryDates);
	const tenantTimezone = expiryWarningsConfig?.tenantTimezone;
	const serviceRunPlanned = expiryWarningsConfig?.serviceRunPlanned;
	const tenantSettings = expiryWarningsConfig?.tenantSettings;
	const resolvedTenantSettings = {
		...tenantSettings,
		expiryDateThresholds: Array.isArray(tenantSettings?.expiryDateThresholds) && tenantSettings.expiryDateThresholds.length > 0
			? tenantSettings.expiryDateThresholds
			: [DEFAULT_EXPIRY_THRESHOLD_DAYS],
	};

	// Iterate dockets
	const products = serviceDockets.reduce((acc, serviceDocket) => {
		let stockPickListItems = serviceDocket.pickList.pickListItems;
		if (shouldComputeMachinePosition) {
			stockPickListItems = calculatePlanogramItemsPositions({
				items: stockPickListItems,
				planogramItemGroups: serviceDocket.machine.planogram.planogramItemGroups,
				planogramItemField: 'planogramItem',
			});
		}
		// Iterate pick list items for each docket
		for	(const pickListItem of stockPickListItems) {
			// Do not create summary items for pick
			// items that have no product.
			if (!pickListItem.product || (pickListItem.quantity + pickListItem.extraQuantity <= 0 && !showAllSelections)) {
				continue;
			}
			// Init product if doesn't exist yet
			if (!acc[pickListItem.product.id]) {
				acc[pickListItem.product.id] = {
					pickListItemIds: [],
					productId: pickListItem.product.id,
					productName: pickListItem.product.name,
					quantity: 0,
					parlevel: 0,
					productImgUrl: pickListItem.product.imageUrl || null,
					pickOrder: pickListItem.pickOrder,
					measurementUnit: pickListItem.product.measurementUnit,
					isIngredient: pickListItem.planogramItem.isIngredient || false,
					pickedQuantity: 0,
					extraQuantity: 0,
					pickedExtraQuantity: 0,
					ifAnySelectedItemPicked: pickListItem.isPicked,
					dockets: {},
				};

				if (shouldComputeMachinePosition) {
					acc[pickListItem.product.id].machinePositions = [];
				}
			}

			// if new docket
			if (!acc[pickListItem.product.id].dockets[serviceDocket.id]) {
				acc[pickListItem.product.id].dockets[serviceDocket.id] = {
					quantity: 0,
					pickedQuantity: 0,
					extraQuantity: 0,
					pickedExtraQuantity: 0,
					pickListItemIds: [],
				};
			}

			// Update product quantity and aggregate machine position
			acc[pickListItem.product.id].pickListItemIds.push(pickListItem.id); // push for performance
			acc[pickListItem.product.id].parlevel += Number(pickListItem.parlevel ?? pickListItem.planogramItemParlevel) || 0;
			acc[pickListItem.product.id].quantity = acc[pickListItem.product.id].quantity + pickListItem.quantity;
			acc[pickListItem.product.id].extraQuantity = acc[pickListItem.product.id].extraQuantity + pickListItem.extraQuantity;
			acc[pickListItem.product.id].dockets[serviceDocket.id].quantity = acc[pickListItem.product.id].dockets[serviceDocket.id].quantity + pickListItem.quantity;
			acc[pickListItem.product.id].dockets[serviceDocket.id].extraQuantity = acc[pickListItem.product.id].dockets[serviceDocket.id].extraQuantity + pickListItem.extraQuantity;
			acc[pickListItem.product.id].ifAnySelectedItemPicked = acc[pickListItem.product.id].ifAnySelectedItemPicked || pickListItem.isPicked;

			// needed pickListItemIds for every docket due to grid summary view
			acc[pickListItem.product.id].dockets[serviceDocket.id].pickListItemIds.push(pickListItem.id);
			if (pickListItem.isPicked) {
				acc[pickListItem.product.id].pickedQuantity = acc[pickListItem.product.id].pickedQuantity + pickListItem.quantity;
				acc[pickListItem.product.id].dockets[serviceDocket.id].pickedQuantity =	acc[pickListItem.product.id].dockets[serviceDocket.id].pickedQuantity + pickListItem.quantity;
			}
			if (pickListItem.extraQuantityIsPicked) {
				acc[pickListItem.product.id].pickedExtraQuantity = acc[pickListItem.product.id].pickedExtraQuantity + pickListItem.extraQuantity;
				acc[pickListItem.product.id].dockets[serviceDocket.id].pickedExtraQuantity = acc[pickListItem.product.id].dockets[serviceDocket.id].pickedExtraQuantity + pickListItem.extraQuantity;
			}
			if (shouldComputeMachinePosition) {
				acc[pickListItem.product.id].machinePositions.push(pickListItem.planogramItem.position);
			}

			if (shouldComputeExpiryWarnings) {
				const { machine } = serviceDocket || {};
				const isMarket = machine?.type === POS_TYPE.MARKET;

				const { expiryDates, quantity } = pickListItem.planogramItem || {};
				const expiryDatesArray = getExpiryDatesArray({ expiryDates, quantity, isMarket });

				const {
					expiredProductsCount,
					expireTodayCount,
					hasExpiryDatesNotFilledCount,
					productsExpiringWithinXDaysCount,
				} = expiryDatesArray.length > 0
					? getExpiryDatesBlockers(expiryDatesArray, resolvedTenantSettings, {
						referenceDate: serviceRunPlanned,
						timezone: tenantTimezone,
					})
					: {};

				const {
					expiringBeforeNextServiceCount,
					nextServiceDate,
					soonestExpiryBeforeNextService,
				} = shouldShowExpiryBeforeNextServiceWarning({ tenantSettings: resolvedTenantSettings, product: pickListItem.product })
					? getExpiringBeforeNextService({
						expiryDates,
						quantity,
						isMarket,
						tenantTimezone,
						serviceRunPlanned,
						serviceFrequencyDays: machine?.averageDaysSinceLastService,
					})
					: {
						expiringBeforeNextServiceCount: 0,
						nextServiceDate: null,
						soonestExpiryBeforeNextService: null,
					};

				const nextWarnings = {
					expiredProductsCount: expiredProductsCount || 0,
					expireTodayCount: expireTodayCount || 0,
					unknownExpiryCount: hasExpiryDatesNotFilledCount || 0,
					productsExpiringWithinXDaysCount: productsExpiringWithinXDaysCount || 0,
					expiringBeforeNextServiceCount: expiringBeforeNextServiceCount || 0,
					nextServiceDate,
					soonestExpiryBeforeNextService,
				};
				nextWarnings.status = getExpiryWarningsStatus(nextWarnings);

				acc[pickListItem.product.id].dockets[serviceDocket.id].expiryWarnings = mergeExpiryWarnings(
					acc[pickListItem.product.id].dockets[serviceDocket.id].expiryWarnings,
					nextWarnings,
				);
			}

		}
		return acc;
	}, {});

	// Then convert back to an array
	let productsArray = Object.values(products);

	// Compute "average" position if needed
	if (shouldComputeMachinePosition) {
		productsArray = productsArray.map(product => {
			product.position = product.machinePositions.reduce((sum, item) => sum + item, 0) / product.machinePositions.length;
			return product;
		});
	}

	if (shouldComputeExpiryWarnings) {
		productsArray = productsArray.map(product => {
			const mergedWarnings = Object.values(product.dockets || {})
				.map(docket => docket.expiryWarnings)
				.filter(Boolean)
				.reduce((acc, warning) => mergeExpiryWarnings(acc, warning), {});

			product.expiryWarnings = mergedWarnings;

			return product;
		});
	}
	return productsArray;
}
